<template>
  <el-header class="page-header" height="48px">
    <div class="page-header-content">
      <div class="page-header-left">
        <router-link to="/" class="logo-link">
          <span class="logo-stack">
            <img src="@/assets/StationOne-ByKochava-White.png" alt="StationOne" class="logo" />
          </span>
        </router-link>
        <nav v-if="!onboarding" class="page-header-nav">
          <router-link :to="{ name: 'Apps' }" class="nav-link" :class="{ active: isIntegrations }">{{ $t('nav.connectors') }}</router-link>
          <router-link :to="{ name: 'Library' }" class="nav-link" :class="{ active: isLibrary }">{{ $t('nav.marketplace') }}</router-link>
          <router-link
            v-if="authStore.isAuthenticated"
            :to="{ name: 'Playbooks' }"
            class="nav-link"
            :class="{ active: isPlaybooks }"
          >{{ $t('nav.playbooks') }}</router-link>
        </nav>
      </div>
      <div class="page-header-right">
        <span v-if="onboarding" class="page-header-label">{{ $t('onboarding.label') }}</span>
        <template v-else>
          <ThemeToggle />
          <AuthHeader />
        </template>
      </div>
    </div>
  </el-header>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { ElHeader } from 'element-plus'
import { useAuthStore } from '@/stores/auth'
import AuthHeader from './AuthHeader.vue'
import ThemeToggle from './ThemeToggle.vue'

defineProps<{ onboarding?: boolean }>()

const route = useRoute()
const authStore = useAuthStore()

const isIntegrations = computed(() => {
  const name = route.name as string
  return name === 'Apps' || name === 'App' || name === 'Connect'
})
// Playbooks = the hub + its two personal views: the user's published
// shared-artifacts pages (/library/artifacts...), content shared TO the user
// (/library/shared), and the hub landing itself (/library/playbooks-hub).
const isPlaybooks = computed(() =>
  route.path.startsWith('/library/artifacts')
  || route.path.startsWith('/library/shared')
  || route.path.startsWith('/library/playbooks-hub'),
)
// Marketplace covers the rest of /library, but NOT the Playbooks routes.
const isLibrary = computed(() =>
  route.path.startsWith('/library') && !isPlaybooks.value,
)
</script>

<style scoped>
.page-header {
  background: #00001f;
  padding: 0 !important;  /* Override Element Plus default padding */
}

.page-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
}

.page-header-left {
  display: flex;
  align-items: center;
  gap: 24px;
}

.page-header-nav {
  display: flex;
  align-items: center;
  gap: 4px;
}

.nav-link {
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  font-size: 12px;
  font-weight: 400;
  padding: 4px 8px;
  border-radius: 0;
  transition: color 0.2s, background-color 0.2s;
}

.nav-link:hover {
  color: rgba(255, 255, 255, 0.9);
  background-color: transparent;
}

.nav-link.active {
  color: rgba(255, 255, 255, 0.72);
  background-color: transparent;
}


.logo-link {
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: opacity 0.2s;
  text-decoration: none;
}

.logo-link:hover {
  opacity: 0.8;
}

.logo-stack {
  display: flex;
}

.logo {
  height: 34px;
  width: auto;
  display: block;
}

.page-header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-header-label {
  color: var(--color-on-primary);
  font: var(--text-label-md);
}

/* Phones: the logo, nav, theme toggle and auth links overflowed ~105px at
   390px, which widened every page's layout viewport (pages rendered zoomed
   out). Tighten spacing and let the nav shrink/scroll instead. */
@media (max-width: 600px) {
  .page-header-content {
    gap: 8px;
    padding: 8px 12px;
  }

  .page-header-left {
    gap: 8px;
    min-width: 0;
  }

  .page-header-nav {
    min-width: 0;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .page-header-nav::-webkit-scrollbar {
    display: none;
  }

  .nav-link {
    padding: 4px 6px;
    white-space: nowrap;
  }

  .logo {
    height: 26px;
  }

  .page-header-right {
    flex-shrink: 0;
    gap: 8px;
  }
}
</style>
